import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { canonicalHash } from '@/api/mock/sign';
import { signHashWithFounderKey } from '@/api/real/founderSign';

// ── Types ────────────────────────────────────────────────────────────────────

interface PeriodCloseResponse {
  close_id: string;
  period_type: string;
  period: string;
  status: string;
  closed_at: string | null;
  closed_by: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function closeStatusLabel(s: string): string {
  switch (s) {
    case 'open': return 'Open';
    case 'closing': return 'Closing';
    case 'closed': return 'Closed';
    case 'approved': return 'Approved';
    default: return s;
  }
}

function statusColor(s: string): string {
  switch (s) {
    case 'open': return '#fbbf24';
    case 'closing': return '#a78bfa';
    case 'closed': return '#7dd3fc';
    case 'approved': return '#34d399';
    default: return 'var(--omega-ink-subtle)';
  }
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceMonthClosePage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const qc = useQueryClient();

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  const { data: listData, isPending, isError } = useQuery({
    queryKey: ['finance', 'period-closes'],
    queryFn: () => apiFetch<{ closes: PeriodCloseResponse[]; total: number }>('/finance/period-closes'),
    refetchInterval: 30_000,
  });

  // Find the close record for the selected period from the full list.
  const data = listData?.closes.find(c => c.period === period) ?? null;

  const approveMutation = useMutation({
    mutationFn: async () => {
      const body = { note: null };
      const sig = await signHashWithFounderKey(canonicalHash(body));
      return apiFetch<{ id: string; action: string }>(`/finance/month-close/${encodeURIComponent(period)}/approve`, {
        method: 'POST',
        body,
        headers: { 'X-Founder-Signature': sig },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'period-closes'] }),
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Month-end close' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Month-end close
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>Period:</label>
          <input
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            style={{ ...inputStyle, width: 100 }}
          />
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {data && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 6, padding: '14px 18px', minWidth: 160,
            }}>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Status</div>
              <div style={{ fontSize: '14pt', fontWeight: 700, color: statusColor(data.status) }}>
                {closeStatusLabel(data.status)}
              </div>
            </div>
            {data.closed_at && (
              <div style={{
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6, padding: '14px 18px', minWidth: 160,
              }}>
                <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Closed</div>
                <div style={{ fontSize: '10pt', color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)' }}>
                  {fmtDate(data.closed_at)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {data && data.status !== 'approved' && (
        <button
          onClick={() => approveMutation.mutate()}
          disabled={approveMutation.isPending}
          style={{
            background: 'rgba(125,211,252,0.12)', border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4, color: '#7dd3fc', fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt', fontWeight: 700, padding: '7px 18px', cursor: 'pointer',
          }}
        >
          {approveMutation.isPending ? 'Approving…' : 'Confirm close'}
        </button>
      )}
      {approveMutation.isError && (
        <div style={{ color: '#f87171', fontSize: '9pt', marginTop: 8 }}>
          Approval error — verify the signature is valid.
        </div>
      )}
    </div>
  );
}
